import { forwardRef } from "react";
import { Instagram, Send, MessageSquare, Phone, Globe } from "lucide-react";
import { cn } from "@/lib/cn";
import type { ChannelType } from "@/modules/chatbot/types";

/**
 * ChannelTypeBadge — multi-channel ko'rsatkich.
 *
 * Token-aware (--channel-{telegram,instagram,whatsapp,facebook,web}-{bg,fg,border}).
 * Brand ranglar:
 *  - Telegram   — sky/cyan
 *  - Instagram  — pink + orange gradient
 *  - WhatsApp   — emerald
 *  - Messenger  — blue + violet gradient
 *  - Web (fallback) — Aurora violet
 */

interface ChannelMeta {
  label: string;
  icon: typeof Instagram;
  cls: string;
  /** style prop'ga beriladi (gradient bg uchun) */
  style?: React.CSSProperties;
}

const META: Record<ChannelType, ChannelMeta> = {
  INSTAGRAM_DM: {
    label: "Instagram DM",
    icon: Instagram,
    cls: "text-[var(--channel-instagram-fg)] border-[var(--channel-instagram-border)]",
    style: { background: "var(--channel-instagram-bg)" },
  },
  INSTAGRAM_COMMENTS: {
    label: "IG Comments",
    icon: Instagram,
    cls: "text-[var(--channel-instagram-fg-alt)] border-[var(--channel-instagram-border)]",
    style: { background: "var(--channel-instagram-bg)" },
  },
  TELEGRAM: {
    label: "Telegram",
    icon: Send,
    cls: "bg-[var(--channel-telegram-bg)] text-[var(--channel-telegram-fg)] border-[var(--channel-telegram-border)]",
  },
  MESSENGER: {
    label: "Messenger",
    icon: MessageSquare,
    cls: "text-[var(--channel-facebook-fg)] border-[var(--channel-facebook-border)]",
    style: { background: "var(--channel-facebook-bg)" },
  },
  WHATSAPP: {
    label: "WhatsApp",
    icon: Phone,
    cls: "bg-[var(--channel-whatsapp-bg)] text-[var(--channel-whatsapp-fg)] border-[var(--channel-whatsapp-border)]",
  },
};

/** Web (chat widget) fallback — agar kelajakda type'ga qo'shilsa */
const WEB_META: ChannelMeta = {
  label: "Web",
  icon: Globe,
  cls: "bg-[var(--channel-web-bg)] text-[var(--channel-web-fg)] border-[var(--channel-web-border)]",
};

export interface ChannelTypeBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement> {
  type: ChannelType;
  size?: "sm" | "md" | "lg";
  showLabel?: boolean;
  iconOnly?: boolean;
}

const sizeMap = {
  sm: { wrap: "h-5 px-1.5 text-[10px] gap-1", icon: "h-3 w-3" },
  md: { wrap: "h-6 px-2 text-[11px] gap-1.5", icon: "h-3.5 w-3.5" },
  lg: { wrap: "h-7 px-2.5 text-xs gap-2", icon: "h-4 w-4" },
};

export const ChannelTypeBadge = forwardRef<HTMLSpanElement, ChannelTypeBadgeProps>(
  ({ className, type, size = "md", showLabel = true, iconOnly = false, style, ...props }, ref) => {
    const meta = META[type] ?? WEB_META;
    const Icon = meta.icon;
    const s = sizeMap[size];
    return (
      <span
        ref={ref}
        className={cn(
          "inline-flex items-center rounded-full border font-medium leading-none whitespace-nowrap",
          s.wrap,
          meta.cls,
          iconOnly && "!px-0 aspect-square justify-center",
          className,
        )}
        style={{ ...meta.style, ...style }}
        title={meta.label}
        aria-label={meta.label}
        {...props}
      >
        <Icon className={s.icon} aria-hidden />
        {showLabel && !iconOnly && <span>{meta.label}</span>}
      </span>
    );
  },
);
ChannelTypeBadge.displayName = "ChannelTypeBadge";
